/* eslint-disable react/jsx-props-no-spreading */
/**
 *
 * SingleQuestion
 *
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _has from 'lodash/has';
import _isEmpty from 'lodash/isEmpty';
import _isNaN from 'lodash/isNaN';
import Markdown from 'marked-react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import RadioGroup from '@material-ui/core/RadioGroup';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import InputBase from '@material-ui/core/InputBase';
import FormGroup from '@material-ui/core/FormGroup';
import MonthPicker from 'components/MonthPicker';
import TimePicker from 'components/TimePicker';
import { makeSelectTextDirection } from 'containers/Main/selectors';
import QuestionMatrix from './QuestionMatrix';
import QuestionMatrixCheckbox from './QuestionMatrixCheckbox';
import QuestionRating from './QuestionRating';
import QuestionImagePicker from './QuestionImagePicker';
import FormControlOption from './components/FormControlOption';
import './styles.css';

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    width: '100%',
    padding: ({ withinPanel }) => (withinPanel ? '20px 16px' : '20px 40px'),
    color: '#4B4B4B !important',
    [theme.breakpoints.down('600')]: {
      width: '100%',
      padding: ({ withinPanel }) => (withinPanel ? 12 : 20),
    },
  },
  title: {
    flex: 1,
    verticalAlign: 'bottom',
    direction: ({ textDirection }) => textDirection,
    textAlign: ({ textDirection }) => (textDirection === 'rtl' ? 'left' : ''),
  },
  subtitle: {
    flex: 1,
    verticalAlign: 'bottom',
    marginTop: 8,
  },
  relative: {
    position: 'relative',
  },
  group: {
    margin: '8px 0 8px 16px',
    [theme.breakpoints.down('600')]: {
      margin: 0,
      '& label': {
        marginRight: 0,
        marginLeft: 0,
      },
    },
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: 4,
    margin: '8px 0',
    padding: 8,
    border: '1px solid #D8D8D8',
    width: '80%',
    fontSize: 20,
    [theme.breakpoints.down('600')]: {
      width: '100%',
    },
  },
  dropdownContainer: {
    marginTop: 12,
    '& select': {
      height: 'auto',
      fontSize: '1.063rem',
    },
  },
  booleanWrapper: {
    margin: '8px 16px',
  },
  booleanButtonRoot: {
    color: theme.palette.text.primary,
    minWidth: 100,
  },
  booleanButtonSelected: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: '#FFFFFF !important',
  },
  errorMessage: {
    color: '#DF4957',
  },
}));

function SingleQuestion({
  className,
  number,
  title,
  description,
  name,
  type,
  labelTrue,
  labelFalse,
  inputType,
  choices,
  optionsCaption,
  columns,
  rows,
  multiSelect,
  imageFit,
  imageWidth,
  showLabel,
  minRateDescription,
  maxRateDescription,
  rateValues,
  rateMin,
  rateMax,
  withinPanel,
  hasSelectAll,
  selectAllText,
  hasNone,
  noneText,
  hasOther,
  otherText,
  placeHolder,
  validators = [],
  surveyContextProps,
}) {
  const textDirection = useSelector(makeSelectTextDirection());
  const classes = useStyles({ withinPanel, textDirection });
  const {
    answers,
    error,
    onAnswerChange,
    onMatrixChange,
    onMatrixCheckboxChange,
    onCheckboxChange,
  } = surveyContextProps;
  const [hasChanged, setChanged] = useState(false);
  const [hasInternalError, setInternalError] = useState(false);
  const isAllChecked =
    Array.isArray(answers[name]) && answers[name].includes('all');
  const isNoneChecked =
    Array.isArray(answers[name]) && answers[name].includes('none');
  const isOther =
    answers[name] === 'other-option' ||
    (Array.isArray(answers[name]) && answers[name].includes('other-option'));
  const hasError = !hasChanged && error.includes(name);
  const isMatrix = type === 'matrix';

  useEffect(() => {
    setChanged(false);
  }, [error]);

  const handleAnswerChange = (...args) => {
    setChanged(true);
    onAnswerChange(...args);
  };
  const handleMatrixChange = (...args) => {
    // setChanged(true);
    onMatrixChange(...args);
  };
  const handleMatrixCheckboxChange = (...args) => {
    setChanged(true);
    onMatrixCheckboxChange(...args);
  };
  const handleCheckboxChange = (...args) => {
    const [questionName, value, useAll] = args;

    if (
      value === 'all' ||
      value === 'none' ||
      (Array.isArray(answers[name]) && answers[name].includes(value)) ||
      !answers[name]
    ) {
      setChanged(true);
      onCheckboxChange(
        questionName,
        value,
        useAll ? choices.map(item => item.value) : undefined,
      );
    } else {
      const countValidator = validators.find(
        validator => validator.type === 'answercount',
      );
      if (countValidator) {
        const { maxCount } = countValidator;
        if (
          !maxCount ||
          (Array.isArray(answers[name]) && answers[name].length < maxCount)
        ) {
          setChanged(true);
          onCheckboxChange(
            questionName,
            value,
            useAll ? choices.map(item => item.value) : undefined,
          );
        }
      } else {
        setChanged(true);
        onCheckboxChange(
          questionName,
          value,
          useAll ? choices.map(item => item.value) : undefined,
        );
      }
    }
  };

  const handleMultipleInputChange = (idx, value) => {
    const values = answers[name] || Array.from({ length: rows }, () => '');
    values[idx] = value;
    onAnswerChange(name, values);
  };

  const renderOptions = () => {
    if (type === 'imagepicker') {
      const props = {
        name,
        choices,
        answers: answers[name],
        multiSelect,
        imageFit,
        imageWidth,
        showLabel,
        onChange: handleAnswerChange,
        onCheckboxChange: handleCheckboxChange,
      };
      return <QuestionImagePicker {...props} />;
    }
    if (type === 'dropdown') {
      return (
        <TextField
          aria-label={title}
          name={name}
          onChange={e => handleAnswerChange(name, e.target.value)}
          value={answers[name] || ''}
          classes={{
            root: classes.dropdownContainer,
          }}
          select
          SelectProps={{
            native: true,
          }}
          size="small"
          type="text"
          variant="outlined"
          autoComplete="off"
        >
          {optionsCaption && (
            <option value="" disabled>
              {optionsCaption}
            </option>
          )}
          {choices.map(item => {
            const label = item.text || item;
            const value = item.value || item;

            return (
              <option value={value} key={`${title}/${value}`}>
                {label}
              </option>
            );
          })}
        </TextField>
      );
    }
    if (type === 'boolean') {
      return (
        <ToggleButtonGroup
          value={answers[name]}
          exclusive
          onChange={(_, value) => handleAnswerChange(name, value)}
          classes={{
            root: classes.booleanWrapper,
          }}
          size="small"
        >
          <ToggleButton
            value="0"
            classes={{
              root: classes.booleanButtonRoot,
              selected: classes.booleanButtonSelected,
            }}
          >
            {labelFalse}
          </ToggleButton>
          <ToggleButton
            value="1"
            classes={{
              root: classes.booleanButtonRoot,
              selected: classes.booleanButtonSelected,
            }}
          >
            {labelTrue}
          </ToggleButton>
        </ToggleButtonGroup>
      );
    }
    if (type === 'radiogroup') {
      return (
        <RadioGroup
          aria-label={title}
          name={name}
          className={classes.group}
          onChange={e => handleAnswerChange(name, e.target.value)}
        >
          {choices.map(el => (
            <FormControlOption
              key={`${title}/${el.value}`}
              value={el.value}
              checked={answers[name] === el.value}
              label={<Markdown isInline>{el.text}</Markdown>}
              type="radio"
            />
          ))}
          {hasOther && (
            <FormControlOption
              key={`${title}/other`}
              value="other-option"
              label={<Markdown isInline>{otherText}</Markdown>}
              checked={isOther}
              type="radio"
            />
          )}
          {isOther && (
            <InputBase
              type="text"
              className={classes.input}
              value={answers[`other-option/${name}`] || ''}
              onChange={e =>
                handleAnswerChange(`other-option/${name}`, e.target.value)
              }
            />
          )}
        </RadioGroup>
      );
    }
    if (type === 'text') {
      let finalInputType = inputType;

      const customProps = {};
      const customConfig = {};
      let errorComponent = null;
      if (inputType === 'number') {
        finalInputType = 'text';

        if (!_isEmpty(validators)) {
          const numericValidator = validators.find(
            validator => validator.type === 'numeric',
          );
          if (_has(numericValidator, 'minValue')) {
            customConfig.min = numericValidator.minValue;
          }
          if (_has(numericValidator, 'maxValue')) {
            customConfig.max = numericValidator.maxValue;
          }

          const maxLength = Math.max(
            _has(customConfig, 'min') ? customConfig.min.toString().length : 0,
            _has(customConfig, 'max') ? customConfig.max.toString().length : 0,
          );
          if (maxLength > 0) {
            customProps.maxlength = maxLength;
          }

          if (hasError || hasInternalError) {
            const parsedValue = Number(answers[name]) || 0;
            if (
              ((_has(customConfig, 'min') && parsedValue < customConfig.min) ||
                (_has(customConfig, 'max') &&
                  parsedValue > customConfig.max)) &&
              !!numericValidator.text
            ) {
              errorComponent = (
                <Typography
                  variant="overline"
                  component="p"
                  className={classes.errorMessage}
                >
                  {numericValidator.text}
                </Typography>
              );
            }
          }
        }
      }
      const onChange = e => {
        if (inputType !== 'number') {
          handleAnswerChange(name, e.target.value);
        } else if (!_isNaN(Number(e.target.value))) {
          const maxLength = Math.max(
            _has(customConfig, 'min') ? customConfig.min.toString().length : 0,
            _has(customConfig, 'max') ? customConfig.max.toString().length : 0,
          );

          if (e.target.value.length >= maxLength) {
            const parsedValue = Number(e.target.value);

            if (
              maxLength > 0 &&
              (e.target.value.length > maxLength ||
                (_has(customConfig, 'min') && parsedValue < customConfig.min) ||
                (_has(customConfig, 'max') && parsedValue > customConfig.max))
            ) {
              setInternalError(true);
            } else {
              setInternalError(false);
            }
          } else if (hasInternalError) {
            setInternalError(false);
          }

          handleAnswerChange(name, e.target.value.replace(/[^0-9.-]/g, ''));
        }
      };

      if (finalInputType === 'month') {
        return (
          <MonthPicker
            value={answers[name]}
            placeholder={placeHolder}
            onChange={dateString => handleAnswerChange(name, dateString)}
            disableFutureDates
            assessment
            preventDisabledDates
          />
        );
      }
      if (finalInputType === 'time') {
        return (
          <TimePicker
            value={answers[name]}
            placeholder={placeHolder}
            onChange={dateString => handleAnswerChange(name, dateString)}
          />
        );
      }
      return (
        <>
          <InputBase
            type={finalInputType}
            className={classes.input}
            value={answers[name] || ''}
            onChange={onChange}
            placeholder={placeHolder}
            inputProps={{ 'aria-label': 'Answer', ...customProps }}
          />
          {errorComponent}
        </>
      );
    }
    if (type === 'comment') {
      return (
        <InputBase
          type="text"
          className={classes.input}
          multiline
          rows={rows || 4}
          placeholder={placeHolder}
          value={answers[name] || ''}
          onChange={e => handleAnswerChange(name, e.target.value)}
          inputProps={{ 'aria-label': 'Answer' }}
        />
      );
    }
    if (type === 'multiple comment') {
      return Array.from({ length: rows || 0 }, (_, idx) => (
        <InputBase
          key={idx}
          type="text"
          className={classes.input}
          multiline
          placeholder={`${idx + 1}.`}
          value={_get(answers[name], idx, '')}
          onChange={e => handleMultipleInputChange(idx, e.target.value)}
          inputProps={{ 'aria-label': 'Answer' }}
        />
      ));
    }
    if (type === 'matrix') {
      const props = {
        name,
        columns,
        rows,
        answers: answers[name],
        error,
        onMatrixChange: handleMatrixChange,
      };
      return <QuestionMatrix {...props} />;
    }
    if (type === 'matrixdropdown') {
      const props = {
        name,
        columns,
        rows,
        answers: answers[name],
        onMatrixCheckboxChange: handleMatrixCheckboxChange,
      };
      return <QuestionMatrixCheckbox {...props} />;
    }
    if (type === 'rating') {
      const props = {
        name,
        minRateDescription,
        maxRateDescription,
        rateValues,
        rateMin,
        rateMax,
        answers: answers[name],
        onChange: handleAnswerChange,
      };
      return <QuestionRating {...props} />;
    }
    if (type === 'checkbox') {
      return (
        <FormGroup className={classes.group}>
          {choices.map(el => {
            let checked = false;
            if (
              answers[name] &&
              Array.isArray(answers[name]) &&
              answers[name].includes(el.value)
            )
              checked = true;
            return (
              <FormControlOption
                key={`${title}/${el.value}`}
                value={el.value}
                label={<Markdown isInline>{el.text}</Markdown>}
                checked={checked}
                onChange={e =>
                  handleCheckboxChange(name, e.target.value, hasSelectAll)
                }
                type="checkbox"
              />
            );
          })}
          {hasOther && (
            <FormControlOption
              value="other-option"
              label={<Markdown isInline>{otherText}</Markdown>}
              checked={isOther}
              onChange={e =>
                handleCheckboxChange(name, e.target.value, hasSelectAll)
              }
              type="checkbox"
            />
          )}
          {isOther && (
            <InputBase
              type="text"
              className={classes.input}
              value={answers[`other-option/${name}`] || ''}
              onChange={e =>
                handleAnswerChange(`other-option/${name}`, e.target.value)
              }
            />
          )}
          {hasSelectAll && (
            <FormControlOption
              value="all"
              label={<Markdown isInline>{selectAllText}</Markdown>}
              checked={isAllChecked}
              onChange={e =>
                handleCheckboxChange(name, e.target.value, hasSelectAll)
              }
              type="checkbox"
            />
          )}
          {hasNone && (
            <FormControlOption
              value="none"
              label={<Markdown isInline>{noneText}</Markdown>}
              checked={isNoneChecked}
              onChange={e =>
                handleCheckboxChange(name, e.target.value, hasSelectAll)
              }
              type="checkbox"
            />
          )}
        </FormGroup>
      );
    }

    return null;
  };

  return (
    <Grid
      item
      xs={12}
      className={_classNames(classes.container, className)}
      id={`q-${name}`}
    >
      <Grid container>
        <Grid item xs={12}>
          {(title || !!number) && (
            <Typography
              variant="h6"
              className={_classNames({
                [classes.title]: true,
                [classes.relative]: !isMatrix,
              })}
              tabIndex={0}
            >
              {!!number && `${number}. `}
              {title && <Markdown isInline>{title}</Markdown>}
              {hasError && !isMatrix && <span className="pulsating-circle" />}
            </Typography>
          )}
          {!_isEmpty(description) && (
            <Typography
              variant="body2"
              className={_classNames({
                [classes.subtitle]: true,
                [classes.relative]: !isMatrix,
              })}
            >
              <Markdown isInline>{description}</Markdown>
            </Typography>
          )}
        </Grid>
        <Grid item xs={12}>
          {renderOptions()}
        </Grid>
      </Grid>
    </Grid>
  );
}

SingleQuestion.propTypes = {
  number: PropTypes.number,
  title: PropTypes.string,
  description: PropTypes.string,
  name: PropTypes.string,
  type: PropTypes.string,
  inputType: PropTypes.string,
  choices: PropTypes.array,
  optionsCaption: PropTypes.string,
  columns: PropTypes.array,
  rows: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  multiSelect: PropTypes.bool,
  imageFit: PropTypes.string,
  imageWidth: PropTypes.number,
  showLabel: PropTypes.bool,
  minRateDescription: PropTypes.string,
  maxRateDescription: PropTypes.string,
  rateValues: PropTypes.array,
  rateMin: PropTypes.number,
  rateMax: PropTypes.number,
  withinPanel: PropTypes.bool,
};

export default SingleQuestion;
